import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { PlayCircle, Loader2, GitCompareArrows } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";
import usePageSeo from "@/hooks/usePageSeo";

const PALETTE = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];
const fmt$ = (v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (v) => `${Number(v).toFixed(2)}%`;

export default function Compare() {
  const [portfolios, setPortfolios] = useState([]);
  const [selected, setSelected] = useState({});
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const nav = useNavigate();
  usePageSeo({
    title: "Compare portfolios — Dividend Portfolio Planner",
    description: "Stand your saved dividend portfolios side by side on the same start date and DRIP settings. Compare CAGR, income, and drawdown.",
    path: "/compare",
  });

  useEffect(() => {
    api.get("/portfolios").then((r) => setPortfolios(r.data)).catch(() => toast.error("Load failed"));
  }, []);

  const selectedList = useMemo(() => portfolios.filter((p) => selected[p.id]), [portfolios, selected]);

  const run = async () => {
    if (selectedList.length < 1) { toast.error("Pick at least one portfolio"); return; }
    setRunning(true); setResults({});
    try {
      const settled = await Promise.all(
        selectedList.map((p) =>
          api.post(`/portfolios/${p.id}/backtest`).then((r) => [p.id, r.data]).catch(() => [p.id, null])
        )
      );
      const map = Object.fromEntries(settled);
      setResults(map);
      const failed = Object.entries(map).filter(([, v]) => !v).length;
      if (failed) toast.warning(`${failed} portfolio(s) failed to backtest`);
      else toast.success("All backtests complete");
    } finally { setRunning(false); }
  };

  const merged = useMemo(() => {
    if (Object.keys(results).length === 0) return [];
    const map = {};
    Object.entries(results).forEach(([pid, res]) => {
      if (!res) return;
      const p = portfolios.find((x) => x.id === pid);
      // Use pid to guarantee uniqueness even when two portfolios share a name.
      const key = p ? `${p.name} · ${pid.slice(0, 6)}` : pid.slice(0, 6);
      res.series.forEach((s) => {
        if (!map[s.date]) map[s.date] = { date: s.date };
        map[s.date][key] = s.value;
      });
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [results, portfolios]);

  const seriesKeys = useMemo(
    () => selectedList.map((p) => `${p.name} · ${p.id.slice(0, 6)}`),
    [selectedList],
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Compare</div>
      <h1 className="serif text-4xl sm:text-5xl mt-2">Two ideas, side by side.</h1>
      <p className="text-muted-foreground mt-2 max-w-lg">Pick saved portfolios below and we&apos;ll backtest them together so you can see which strategy compounded harder.</p>

      {portfolios.length === 0 ? (
        <Card className="mt-8 border-dashed">
          <CardContent className="p-10 text-center">
            <p className="text-muted-foreground">You need at least one saved portfolio to compare.</p>
            <Button className="mt-4 rounded-full" onClick={() => nav("/portfolio/new")}>Create one</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {portfolios.map((p) => (
              <Card
                key={p.id}
                data-testid={TEST_IDS.compareCard}
                className={`card-hover cursor-pointer border ${selected[p.id] ? "border-primary" : "border-border"}`}
                onClick={() => setSelected({ ...selected, [p.id]: !selected[p.id] })}
              >
                <CardContent className="p-5 flex items-start gap-3">
                  <Checkbox
                    data-testid={TEST_IDS.compareSelect}
                    checked={!!selected[p.id]}
                    onCheckedChange={(v) => setSelected({ ...selected, [p.id]: !!v })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="serif text-xl">{p.name}</h3>
                      <Badge variant="outline" className="mono text-[10px]">{p.drip ? "DRIP" : "CASH"}</Badge>
                    </div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                      {p.start_date} → {p.end_date}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {p.holdings.slice(0, 5).map((h) => (
                        <span key={h.ticker} className="mono text-[10px] px-1.5 py-0.5 rounded bg-muted border border-border">
                          {h.ticker} {h.weight}%
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-6">
            <Button onClick={run} disabled={running || selectedList.length === 0} className="rounded-full">
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comparing…</> : <><GitCompareArrows className="w-4 h-4 mr-2" /> Compare {selectedList.length || ""} portfolios</>}
            </Button>
          </div>

          {seriesKeys.length > 0 && merged.length > 0 && (
            <Card className="mt-8 animate-draw">
              <CardContent className="p-6">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Portfolio value over time</div>
                <h3 className="serif text-2xl mt-1">Side by side</h3>
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart data={merged}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} minTickGap={40} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {seriesKeys.map((k, i) => (
                      <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {Object.keys(results).length > 0 && (
            <div className="mt-6 overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-left">
                    <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground">Portfolio</th>
                    <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">Final value</th>
                    <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">Total return</th>
                    <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">CAGR</th>
                    <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">Dividends</th>
                    <th className="px-4 py-2 mono text-[11px] uppercase tracking-widest text-muted-foreground text-right">YoC</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedList.map((p) => {
                    const r = results[p.id];
                    if (!r) return (
                      <tr key={p.id} className="border-t border-border">
                        <td className="px-4 py-2 serif">{p.name}</td>
                        <td className="px-4 py-2 mono text-right text-destructive" colSpan={5}>failed</td>
                      </tr>
                    );
                    return (
                      <tr key={p.id} className="border-t border-border">
                        <td className="px-4 py-2 serif">{p.name}</td>
                        <td className="px-4 py-2 mono text-right">{fmt$(r.summary.final_value)}</td>
                        <td className="px-4 py-2 mono text-right">{fmtPct(r.summary.total_return_pct)}</td>
                        <td className="px-4 py-2 mono text-right">{fmtPct(r.summary.cagr_pct)}</td>
                        <td className="px-4 py-2 mono text-right text-secondary">{fmt$(r.summary.total_dividends)}</td>
                        <td className="px-4 py-2 mono text-right">{fmtPct(r.summary.yield_on_cost_pct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
